<template>
	<div class="c-health-check-tool-container">
		<div class="c-health-check-tool" ref="healthCheckTool">
			<div class="c-health-check-tool__content-wrapper">
				<HealthCheckContentHead
					ref="progress"
					v-bind="contentHead"
					@skip="handleSkip"
				/>
				<input
					:id="`code-${gui}`"
					type="text"
					name="code"
					style="display: none"
					autocomplete="off"
					tabindex="-1"
					ref="code"
					aria-hidden="true"
				/>
				<!-- Single view swaps based on activeIndex -->
				<Transition
					name="fade"
					mode="out-in"
					@after-enter="handleStepEnter"
				>
					<!-- Keyed by activeIndex so transitions run correctly -->
					<div
						:key="state.currentStep"
						class="c-health-check-tool__slide"
					>
						<!-- Intro -->
						<IntroSlide
							v-if="state.isIntroStep"
							key="IntroSlide"
							:introModelSlide="introModelSlide"
							@next="handleNextStep"
						>
							<template #references>
								<slot name="intro-references"></slot>
							</template>
							<template #list-block>
								<slot name="list-block-content"></slot>
							</template>
						</IntroSlide>

						<!-- Question(s) -->
						<StepContainer
							v-else-if="state.isQuestionStep"
							ref="step"
							:labels="labels"
							:goToLastStep="model.goToLastStep"
							@next="handleNextStep"
							@prev="handlePrevStep"
							@setAnswer="handleSetAnswer"
							@submit="handleSubmit"
						/>

						<!-- Consent -->
						<StepConsent
							v-else-if="state.isConsentStep"
							:accept-button-text="
								model.consentForm?.acceptButtonText
							"
							:body-text="model.consentForm?.bodyText"
							:consent-description="
								model.consentForm?.consentDescription
							"
							:consent-type="model.consentForm?.consentType"
							:labels="labels"
							:faq-area1="model.consentForm?.faqArea1"
							:faq-area2="model.consentForm?.faqArea2"
							:headline="model.consentForm?.headline"
							:skip-button-text="
								model.consentForm?.skipButtonText
							"
							@next="handleNextStep"
							@prev="handlePrevStep"
							@seeResults="handleSkip"
							@consent="handleSetConsent"
						/>

						<!-- Personal Info -->
						<PersonalInfoSlide
							v-else-if="state.isPersonalInfoStep"
							key="PersonalInfoSlide"
							:labels="labels"
							@prev="handlePrevStep"
							@submit="handleSubmit"
						>
							<template #references>
								<slot
									name="personal-information-references"
								></slot>
							</template>
						</PersonalInfoSlide>
					</div>
				</Transition>
			</div>
		</div>
		<DsSpinner
			display-mode="default"
			status="loading"
			accessibility-label="loading"
			v-if="state.isBusy"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, useId } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import { useElementVisibility } from '@vueuse/core';
import IntroSlide from './components/introSlide.vue';
import PersonalInfoSlide from './components/personalInfoSlide.vue';
import StepContainer from './components/stepContainer.vue';
import StepConsent from './components/stepConsent.vue';
import HealthCheckContentHead from './components/contentHead.vue';
import { useHealthCheckState } from './useHealthCheckState';
import { DsSpinner } from '@coloplast/design-system';
import { check_tool } from 'shared/services/tracking/toolService';
import {
	track_question_view,
	track_question_continue_click,
	track_question_completed,
} from 'shared/services/tracking/healthCheckTrackingService';
import type { ICheckTool } from 'shared/services/tracking/interfaces/ICheckTool';
import type {
	BaseViewModel,
	Question,
	Scalar,
	SendRequestAnswer,
	SendRequestErrorResponse,
} from './HealthCheckTool.types';

type FocusableInstance = { $el?: HTMLElement };
type HealthCheckContentHeadType = {
	rootEl: HTMLElement | null;
};

const store = useStore();
const {
	initializeSteps,
	nextStep,
	prevStep,
	setAnswer,
	goToStep,
	prefillAnswers,
	resetState,
	setHealthCheckPayload,
	setFormErrors,
	state,
	currentQuestionId,
} = useHealthCheckState();
const props = defineProps<{
	vm: { baseViewModel: BaseViewModel };
}>();
const vm = props.vm;
const model = computed(() => vm.baseViewModel);
initializeSteps(model.value);

const step = ref<FocusableInstance | null>(null);
const code = ref<HTMLInputElement | null>(null);
const userHasConsented = ref(false);
const userHasSkipped = ref(false);

const progressRef = useTemplateRef<HealthCheckContentHeadType>('progress');
const progressElement = computed<HTMLElement | null>(
	() => progressRef.value?.rootEl ?? null,
);
const progressIsVisible = useElementVisibility(progressElement);
const gui = useId();

const labels = computed(() => ({
	close: model.value.closeButtonText,
	next: model.value.nextButtonText,
	prev: model.value.previousButtonText,
	submit: model.value.submitButtonText,
}));

const contentHead = computed(() => ({
	tagLabel: model.value.tagLabel ?? '',
	questionLength: state.questions?.length ?? 0,
	progressbarText: model.value.progressBarAriaLabel ?? '',
	progressBarActiveIndex:
		state.currentStep + (model.value.displayIntroPage ? 0 : 1),
	showProgressBar:
		state.currentStep >= (model.value.displayIntroPage ? 1 : 0),
	isPersonalInfoStep: state.isPersonalInfoStep,
	skipSubmittingContactInfoLabel:
		model.value.skipSubmittingContactInfoLabel ?? '',
	isConsentStep: state.isConsentStep,
}));

const introModelSlide = computed(() => ({
	headline: model.value.introPageHeadline,
	description: model.value.introPageDescription,
	continueButtonText: model.value.introPageContinueButtonText,
	cancelButtonText: model.value.introPageCancelButtonText ?? 'Cancel',
	cancelButtonLink: model.value.introPageCancelButtonLink,
	informationalLabel: model.value.informationalLabel,
	informationalIcon: model.value.informationalIcon,
	...getIntroPageMediaContent(model.value),
}));

onMounted(() => {
	if (state.questions.length > 0) {
		// Initial tracking (if no intro, first question "start")
		if (!model.value.displayIntroPage) {
			check_tool(setTrackingActiveIndexData(state.questions[0], 'start'));
			track_question_view();
		}

		// Prebuild request (+ optional prefill if goToLastStep)
		if (model.value.goToLastStep) {
			prefillAnswers();

			// Jump to last question if requested
			goToStep(
				model.value.displayIntroPage
					? state.questions.length + 1
					: state.questions.length,
			);
		}
	}
});

function getIntroPageMediaContent(model: BaseViewModel) {
	const isVideoMedia = model.vimeoVideoUrl;
	const mediaContent = Object.freeze({
		captionLocalBlock: {
			...model.captionLocalBlock,
		},
	});
	const videoContent = Object.freeze({
		videoUrl: model.vimeoVideoUrl,
		videoDuration: model.duration,
		videoCtaButtonLabel: model.videoCtaLinkLabel,
		videoPreviewImageAltText: model.videoPosterImageAltText,
		videoPreviewImage: model.videoPosterImageUrl,
	});
	const imageContent = Object.freeze({
		videoPreviewImage: model.imageUrl,
		videoPreviewImageAltText: model.imageAltText,
		videoUrl: '',
	});

	return {
		baseViewModel: {
			...mediaContent,
			...(isVideoMedia ? videoContent : imageContent),
		},
	};
}

async function handleStepEnter() {
	// focus the step container's root if present
	await nextTick();
	// tracking "start" events
	trackStepsView();
	const el = (step.value as FocusableInstance | null)?.$el as
		| HTMLElement
		| undefined;
	el?.focus?.();

	if (!progressIsVisible.value) {
		progressRef.value?.rootEl?.scrollIntoView();
	}
}

function handleSetAnswer(data: {
	questionBlockId: Scalar;
	answer: SendRequestAnswer | SendRequestAnswer[];
}) {
	setAnswer(data);
}

function handleSetConsent(consent: boolean) {
	userHasConsented.value = consent;
}

async function handleSkip() {
	userHasSkipped.value = true;
	await handleSubmit();
}

async function handleSubmit(formData?: Record<string, unknown>) {
	trackPersonalInfoOptIn(!!formData);
	state.isBusy = true;

	const codeInput: HTMLInputElement | null = code.value;
	const codeValue = codeInput?.value || '';

	setHealthCheckPayload({
		consentAccepted: userHasConsented.value,
		code: codeValue,
		form:
			formData && !userHasSkipped.value
				? { formFields: formData }
				: undefined,
	});

	try {
		const { data } = await axios.post(
			`${model.value.apiUrl}`,
			state.healthCheckPayload,
		);
		resetState();

		if (data?.showPopup) {
			store.commit('globalCheckout/setLoginModalShow', data.showPopup);
			state.isBusy = false;
			return;
		}

		if (data?.redirectUrl) {
			globalThis.location.assign(`${data.redirectUrl}`);
			return;
		}

		state.isBusy = false;
	} catch (error) {
		const errorData = (error as SendRequestErrorResponse)?.response?.data;
		if (errorData?.isError && (errorData?.fieldErrors || errorData?.generalError)) {
			setFormErrors({
				generalError: errorData.generalError ?? "",
				fieldErrors: errorData.fieldErrors ?? {},
			});
		}
		console.warn(error);
		state.isBusy = false;
	}
}

function handleNextStep() {
	trackNextSteps();
	nextStep();
}

function handlePrevStep() {
	prevStep();
}

function setTrackingConsent(status: 'start' | 'finish') {
	return {
		event: 'check_tool',
		check_name: state.checkTrackingName,
		check_question_name: `Consent: ${state.consentForm?.consentType}`,
		check_question_state: status,
		check_call: 'no_data',
	} as ICheckTool;
}

function setTrackingActiveIndexData(
	indexOrQuestion: number | Question,
	status: 'start' | 'finish',
) {
	const question =
		typeof indexOrQuestion === 'number'
			? state.questions[indexOrQuestion]
			: indexOrQuestion;

	return {
		event: 'check_tool',
		check_name: state.checkTrackingName,
		check_question_name: question?.questionTrackingName,
		check_question_state: status,
		check_call: 'no_data',
	} as ICheckTool;
}

function setTrackingPersonalInformation(
	status: 'start' | 'finish',
	opt_in: 'opt_in' | 'opt_out' | 'no_data',
) {
	return {
		event: 'check_tool',
		check_name: state.checkTrackingName,
		check_question_name: 'opt_in_step',
		check_question_state: status,
		check_call: opt_in,
	} as ICheckTool;
}

function trackNextSteps() {
	const currentQuestion = state.questions.find(
		(question) => question.questionBlockId === currentQuestionId.value,
	);

	if (state.isQuestionStep && currentQuestion) {
		check_tool(setTrackingActiveIndexData(currentQuestion, 'finish'));
		track_question_continue_click();
	} else if (state.isConsentStep) {
		check_tool(setTrackingConsent('finish'));
	}

	if (
		state.isQuestionStep &&
		state.currentQuestionStep === state.questions.length - 1
	) {
		track_question_completed();
	}
}

function trackStepsView() {
	if (state.isQuestionStep) {
		if (
			state.currentQuestionStep >= 0 &&
			state.currentQuestionStep < state.questions.length
		) {
			check_tool(
				setTrackingActiveIndexData(state.currentQuestionStep, 'start'),
			);
			track_question_view();
		}
	} else if (state.isConsentStep) {
		check_tool(setTrackingConsent('start'));
	} else if (state.isPersonalInfoStep) {
		check_tool(setTrackingPersonalInformation('start', 'no_data'));
	}
}

function trackPersonalInfoOptIn(optIn: boolean) {
	if (state.steps.includes('personal')) {
		check_tool(
			setTrackingPersonalInformation(
				'finish',
				optIn ? 'opt_in' : 'opt_out',
			),
		);
	} else {
		const last = state.questions.at(-1);
		if (last) check_tool(setTrackingActiveIndexData(last, 'finish'));
	}
}

defineExpose({
	code,
	handleStepEnter,
	handleSetConsent,
});
</script>

<style scoped lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

@layer base {
	/* Fade transition */
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 300ms ease;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.c-health-check-tool__content-wrapper {
		display: grid;
		gap: ds-space(9);
		padding-block: ds-space(6);
		padding-inline: ds-space(6);
		row-gap: ds-space(9);

		@media (min-width: ds-breakpoint('md')) {
			padding-block-end: ds-space(11);
			padding-block-start: ds-space(10);
			padding-inline: ds-space(11);
		}
	}

	.c-health-check-tool-container {
		background-color: ds-color('bg', 'default');
		border-radius: 16px;
		position: relative;
		overflow: hidden;

		@media (min-width: ds-breakpoint('md')) {
			min-block-size: initial;
		}

		.c-health-check-tool__slide {
			align-items: start;
			background-color: ds-color('bg', 'default');
			display: flex;
			flex-direction: column;
			row-gap: ds-space(9);
		}

		:deep(.c-health-check-tool__slide-head) {
			display: flex;
			justify-content: center;
		}
	}
}
</style>
